import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import StaffLayout from "@/components/StaffLayout";
import HandoverRequestDialog from "@/components/HandoverDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Fuel, StopCircle, UserPlus2, Clock10, X, Check } from "lucide-react";
import { fmtDate, fmtTime } from "@/utils/format";
import { toast } from "sonner";

export default function RunningShift() {
  const { id } = useParams();
  const nav = useNavigate();
  const [shift, setShift] = useState(null);
  const [now, setNow] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = () => api.get(`/shifts/${id}`).then((r) => setShift(r.data));

  useEffect(() => {
    load();
    const t = setInterval(() => setNow(new Date()), 1000);
    // Poll shift status every 4s to detect accept/reject/completion
    const p = setInterval(async () => {
      try {
        const { data } = await api.get(`/shifts/${id}`);
        setShift(data);
        if (data?.status === "completed") {
          clearInterval(p);
          nav(`/shift/${id}/summary`, { replace: true });
        }
      } catch { /* ignore */ }
    }, 4000);
    return () => { clearInterval(t); clearInterval(p); };
    // eslint-disable-next-line
  }, [id]);

  const cancelHandover = async () => {
    try {
      await api.post(`/shifts/${id}/handover-cancel`);
      toast.success("Handover cancelled");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  if (!shift) return <StaffLayout><div className="h-32 animate-pulse bg-slate-100 rounded-2xl" /></StaffLayout>;

  const start = new Date(shift.start_time);
  const elapsedMs = now - start;
  const hh = String(Math.floor(elapsedMs / 3_600_000)).padStart(2, "0");
  const mm = String(Math.floor((elapsedMs % 3_600_000) / 60_000)).padStart(2, "0");
  const ss = String(Math.floor((elapsedMs % 60_000) / 1000)).padStart(2, "0");
  const isRequested = shift.status === "handover_requested";
  const isAccepted = shift.status === "handover_accepted";

  return (
    <StaffLayout>
      <Card className="p-6 rounded-2xl border-slate-200 shadow-sm bg-gradient-to-br from-[color:var(--bpcl-blue)] to-[color:var(--bpcl-blue-dark)] text-white">
        <div className="flex items-center gap-2 text-white/80 text-xs uppercase tracking-wide font-medium">
          <span className={`w-2 h-2 rounded-full ${isAccepted ? "bg-emerald-300" : "bg-[color:var(--bpcl-orange)]"} animate-pulse`} />
          {isRequested ? "Handover requested"
            : isAccepted ? "Handover accepted — complete when ready"
            : "Shift Running"}
        </div>
        <div className="flex items-end justify-between mt-2">
          <div>
            <div className="font-display text-3xl font-bold">{shift.mpd}</div>
            <div className="text-white/80 mt-0.5">Shift {shift.shift_number} · {shift.employee_name}</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-white/70 uppercase tracking-wide">Elapsed</div>
            <div className="font-mono-num text-2xl font-semibold" data-testid="elapsed-time">
              {hh}:{mm}:{ss}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-6 text-sm">
          <div><div className="text-white/70 text-xs">Business Date</div><div className="font-semibold">{fmtDate(shift.business_date)}</div></div>
          <div><div className="text-white/70 text-xs">Started</div><div className="font-semibold">{fmtTime(shift.start_time)}</div></div>
          <div><div className="text-white/70 text-xs">Status</div><div className="font-semibold capitalize">{shift.status.replace("_", " ")}</div></div>
        </div>
      </Card>

      {isRequested && (
        <Card className="p-5 rounded-2xl border-[color:var(--bpcl-orange)] border-2 bg-[color:var(--bpcl-orange)]/5 mt-5" data-testid="handover-pending-card">
          <div className="flex items-start gap-3">
            <span className="w-10 h-10 rounded-full bg-[color:var(--bpcl-orange)]/20 grid place-items-center">
              <Clock10 className="w-5 h-5 text-[color:var(--bpcl-orange-dark)]" />
            </span>
            <div className="flex-1">
              <div className="font-semibold">Waiting for {shift.pending_next_employee_name}…</div>
              <div className="text-sm text-slate-600 mt-0.5">Continue fueling normally. You’ll be notified when they accept.</div>
            </div>
            <Button
              variant="outline"
              onClick={cancelHandover}
              data-testid="cancel-handover-btn"
              className="rounded-full h-9"
            >
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
          </div>
        </Card>
      )}

      {isAccepted && (
        <Card className="p-5 rounded-2xl border-emerald-500 border-2 bg-emerald-50 mt-5" data-testid="handover-accepted-card">
          <div className="flex items-start gap-3">
            <span className="w-10 h-10 rounded-full bg-emerald-500/20 grid place-items-center">
              <Check className="w-5 h-5 text-emerald-700" />
            </span>
            <div className="flex-1">
              <div className="font-semibold">{shift.pending_next_employee_name} is ready to take over</div>
              <div className="text-sm text-slate-600 mt-0.5">
                When you finish the printer receipt, enter closing readings and complete the handover.
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-5 rounded-2xl border-slate-200 mt-5">
        <div className="text-sm text-slate-500 mb-3 flex items-center gap-2">
          <Fuel className="w-4 h-4" /> Opening readings recorded
          {shift.previous_employee_name && (
            <span className="ml-auto text-xs rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-2 py-0.5">
              handover from {shift.previous_employee_name}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          {Object.entries(shift.opening).map(([k, v]) => (
            <div key={k} className="rounded-xl border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">{k.replace("_", " ")}</div>
              <div className="font-mono-num font-semibold mt-0.5">{Number(v).toFixed(2)}</div>
            </div>
          ))}
        </div>
      </Card>

      <div className="mt-6 sticky bottom-4 space-y-3">
        {!isRequested && (
          <Button
            variant="outline"
            onClick={() => setDialogOpen(true)}
            data-testid="request-handover-btn"
            disabled={isAccepted}
            className="w-full h-12 rounded-xl text-base font-semibold border-[color:var(--bpcl-blue)] text-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue)]/5"
          >
            <UserPlus2 className="w-4 h-4 mr-2" /> {isAccepted ? "Handover confirmed" : "Request handover to next employee"}
          </Button>
        )}
        <Button
          onClick={() => nav(`/shift/${id}/end`)}
          data-testid="end-shift-btn"
          className="w-full h-14 rounded-xl bg-[color:var(--bpcl-orange)] hover:bg-[color:var(--bpcl-orange-dark)] text-slate-900 text-base font-bold shadow-lg hover:scale-[1.01] active:scale-[0.99] transition-transform"
        >
          <StopCircle className="w-5 h-5 mr-2" />
          {isAccepted ? "COMPLETE HANDOVER" : "END SHIFT"}
        </Button>
      </div>

      <HandoverRequestDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        currentShift={shift}
        onSent={() => load()}
      />
    </StaffLayout>
  );
}
